import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  ThemeProvider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  List,
  ListItem,
  ListItemIcon,
  Avatar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  MoreHoriz as MoreHorizIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from 'react-i18next';

const SendEmailModal = ({
  open,
  onClose,
  template,
  recruiterId,
  theme,
  candidateInfo = null,
  showTemplateSelection = false,
}) => {
  const [formData, setFormData] = useState({
    recipient_email: "",
    placeholder_values: {},
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [placeholders, setPlaceholders] = useState([]);
  const [renderedEmail, setRenderedEmail] = useState({
    subject: "",
    body: "",
  });

  // State for template selection
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isVisualTemplate, setIsVisualTemplate] = useState(false);

  const selectRef = useRef(null);
  // Ref for iframe to display HTML content
  const iframeRef = useRef(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(""); // Initialize with empty string, not undefined
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // NEW: State for multiple recipients functionality
  const [sendMode, setSendMode] = useState("single"); // "single" or "multiple"
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // NEW: Fetch candidates function
  // Updated fetchCandidates to use Elasticsearch endpoint and support search
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateHasMore, setCandidateHasMore] = useState(true);

  // NEW: Infinite scroll and search state for candidates
  const [candidateContainerRef, setCandidateContainerRef] = useState(null);
  const [loadingMoreCandidates, setLoadingMoreCandidates] = useState(false);
  const [totalCandidateCount, setTotalCandidateCount] = useState(0);

  const fetchCandidates = async (search = "", pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoadingCandidates(true);
      } else {
        setLoadingMoreCandidates(true);
      }

      // Always use Django ORM endpoint for consistent results
      const url = `${process.env.REACT_APP_API_URL}recruiters/${recruiterId}/recruiter_all_candidates_simple/`;
      const params = { page: pageNum, page_size: 5 };
      
      // Add search query if provided
      if (search) {
        params.q = search;
      }
      
      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      let candidatesData = [];
      if (response.data.candidates && Array.isArray(response.data.candidates)) {
        candidatesData = response.data.candidates.map((candidate) => ({
          id: candidate.candidate_id || candidate.id,
          source_id: candidate.source_id,
          candidate_type: candidate.candidate_type,
          email: candidate.email,
          first_name: candidate.first_name,
          last_name: candidate.last_name,
          phone: candidate.phone,
          skills: candidate.skills,
          company: candidate.company || "",
          city: candidate.city || "",
          country: candidate.country || "",
          image: candidate.image || null,
          experiences: candidate.experiences || "",
          educations: candidate.educations || "",
          age: candidate.age || null,
          score_matching: candidate.score_matching || 0,
          status: candidate.status || "",
          source: candidate.source || "",
          cv_file_path: candidate.cv_file_path || "",
          cv_file_name: candidate.cv_file_name || "",
          cv: candidate.cv || "",
          cover_letter: candidate.cover_letter || "",
          linkedin_profile: candidate.linkedin_profile || "",
        }));
      }
      // Append new candidates or set initial list
      if (pageNum === 1) {
        setCandidates(candidatesData);
      } else {
        setCandidates((prev) => [...prev, ...candidatesData]);
      }
      setCandidateHasMore(!!response.data.next);
      setCandidatePage(pageNum);

      // Store total count from API response
      if (response.data.count !== undefined) {
        setTotalCandidateCount(response.data.count);
      }

      
    } catch (error) {
      console.error("Error fetching candidates:", error);
      if (pageNum === 1) {
        setCandidates([]);
      }
      setCandidateHasMore(false);
    } finally {
      setLoadingCandidates(false);
      setLoadingMoreCandidates(false);
    }
  };

  // Fetch candidates when modal opens, recruiterId changes, sendMode changes, or search changes
  useEffect(() => {
    if (open && sendMode === "multiple" && recruiterId) {
      fetchCandidates(candidateSearch, 1);
    }
  }, [open, sendMode, recruiterId, candidateSearch]);

  // Fetch templates if showTemplateSelection is true
  useEffect(() => {
    if (showTemplateSelection && recruiterId) {
      // Start with page 1
      setPage(1);
      fetchTemplates(1, false);
    } else if (template) {
      // If a template is provided directly
      setSelectedTemplate(template);
      setSelectedTemplateId(template.id || "");
      setIsVisualTemplate(!!template.design);
    }
  }, [showTemplateSelection, recruiterId]);

  // Function to fetch templates
  // Modified fetchTemplates function in SendEmailModal component
  const fetchTemplates = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoadingTemplates(true);
      } else {
        setIsLoadingMore(true);
      }

      const url = `${process.env.REACT_APP_API_URL}recruiters/${recruiterId}/templates/`;

      const response = await axios.get(url, {
        params: { page: pageNum, page_size: 10 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      // Handle the response data
      if (response.data.results && Array.isArray(response.data.results)) {
        // Check if we have templates in the results
        if (response.data.results.length > 0) {
          if (append) {
            // Append new templates to existing ones
            setAvailableTemplates((prev) => {
              const combinedTemplates = [...prev, ...response.data.results];
              return combinedTemplates;
            });
          } else {
            // Set initial templates
            setAvailableTemplates(response.data.results);
          }

          // Check if there are more templates to load
          setHasMore(!!response.data.next);
        } else {
          setHasMore(false);
        }
      } else if (Array.isArray(response.data)) {
        // Handle non-paginated array response
        if (append) {
          setAvailableTemplates((prev) => [...prev, ...response.data]);
        } else {
          setAvailableTemplates(response.data);
        }
        setHasMore(false);
      } else {
        if (!append) {
          setAvailableTemplates([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      if (!append) {
        setAvailableTemplates([]);
      }
      setHasMore(false);
    } finally {
      setLoadingTemplates(false);
      setIsLoadingMore(false);
    }
  };

  const handleCreateWaitList = async () => {
    try {
      const payload = {
        email: candidateInfo.email,
        application_scraped: candidateInfo.applicationId,
        recruiter: recruiterId,
      };

      console.log("Creating waitlist with payload:", payload);

      // Send email using backend API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}authentication/application/waitlist/`,
        payload
      );

      // Handle successful waitlist creation
      console.log('WaitList created successfully!');
      return response.data;
    } catch (error) {
      // Handle any errors in waitlist creation silently
      // Don't show alert or throw error to prevent interference with main email success flow
      console.error("WaitList creation failed:", error);
      console.error("WaitList creation error details:", error.response?.data);
      // Return null instead of throwing to allow email success to proceed
      return null;
    }
  };

  const handleMenuScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    const scrollPosition = scrollHeight - scrollTop - clientHeight;

    // Check if scrolled to bottom (with a small buffer)
    if (scrollPosition < 5) {
      if (hasMore && !isLoadingMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchTemplates(nextPage, true);
      }
    }
  };

  // Handle template selection change
  const handleTemplateChange = (event) => {
    const value = event.target.value;

    // Make sure we always set to empty string if undefined/null
    setSelectedTemplateId(value || "");

    if (value) {
      const template = availableTemplates.find((t) => t.id === value);
      if (template) {
        setSelectedTemplate(template);
        setIsVisualTemplate(!!template.design);
      } else {
        // Fallback to prevent undefined value
        setSelectedTemplate(null);
        setSelectedTemplateId("");
      }
    } else {
      setSelectedTemplate(null);
      setIsVisualTemplate(false);
    }
  };

  const loadMoreTemplates = () => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTemplates(nextPage, true);
    }
  };

  // NEW: Handle send mode change
  const handleSendModeChange = (event) => {
    setSendMode(event.target.value);
    setSelectedCandidates([]);
    setFormData((prev) => ({
      ...prev,
      recipient_email:
        event.target.value === "single" ? candidateInfo?.email || "" : "",
    }));
  };

  // NEW: Handle candidate selection
  const handleCandidateSelection = (candidateId) => {
    setSelectedCandidates((prev) => {
      if (prev.includes(candidateId)) {
        return prev.filter((id) => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleSelectAllCandidates = async () => {
    if (!Array.isArray(candidates)) return;

    // If all candidates are already selected, deselect all
    if (selectedCandidates.length > 0) {
      setSelectedCandidates([]);
      return;
    }

    // Fetch ALL candidates (not just visible ones)
    try {
      setLoadingCandidates(true);
      let allCandidates = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const url = `${process.env.REACT_APP_API_URL}recruiters/${recruiterId}/recruiter_all_candidates_simple/`;
        const params = { page: page, page_size: 100 }; // Use larger page size for bulk fetch

        if (candidateSearch) {
          params.q = candidateSearch;
        }

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });

        if (
          response.data.candidates &&
          Array.isArray(response.data.candidates)
        ) {
          const candidatesData = response.data.candidates.map((candidate) => ({
            id: candidate.candidate_id || candidate.id,
            email: candidate.email,
            first_name: candidate.first_name,
            last_name: candidate.last_name,
          }));
          allCandidates = [...allCandidates, ...candidatesData];
        }

        hasMore = !!response.data.next;
        page++;
      }

             // Select all candidate IDs
       const allCandidateIds = allCandidates.map((c) => c.id);
       setSelectedCandidates(allCandidateIds);
     } catch (error) {
       console.error("Error fetching all candidates for selection:", error);
       // Fallback to selecting only visible candidates
       setSelectedCandidates(candidates.map((c) => c.id));
     } finally {
       setLoadingCandidates(false);
     }
   };



  // Extract placeholders from template when it changes
  useEffect(() => {
    if (selectedTemplate) {
      console.log("=== PLACEHOLDER EXTRACTION DEBUG START ===");
      console.log("🔍 STEP 1: Template Analysis");
      console.log("Selected template:", selectedTemplate);
      console.log("Template ID:", selectedTemplate.id);
      console.log("Template name:", selectedTemplate.name);
      console.log("Template subject:", selectedTemplate.subject);
      console.log("Template body:", selectedTemplate.body);
      console.log("Template body (first 500 chars):", selectedTemplate.body.substring(0, 500));
      console.log("Template body (full length):", selectedTemplate.body.length, "characters");
      console.log("Is visual template:", isVisualTemplate);
      console.log("Template design:", selectedTemplate.design);
      
      // Debug: Check for all possible placeholder patterns in the template
      console.log("🔍 STEP 2: Pattern Analysis");
      console.log("=== CHECKING ALL POSSIBLE PLACEHOLDER PATTERNS ===");
      const allPatterns = [
        { name: "Visual pattern {{word}}", regex: /\{\{([a-zA-ZÀ-ÿ][a-zA-Z0-9_À-ÿ\s']*)\}\}/g },
        { name: "Standard pattern {word}", regex: /{([a-zA-ZÀ-ÿ][a-zA-Z0-9_À-ÿ\s']*)}/g },
        { name: "Any {{...}} pattern", regex: /\{\{[^}]+\}\}/g },
        { name: "Any {...} pattern", regex: /{[^}]+}/g }
      ];
      
      allPatterns.forEach(pattern => {
        const matches = [...(selectedTemplate.subject.match(pattern.regex) || [])];
        const bodyMatches = [...(selectedTemplate.body.match(pattern.regex) || [])];
        console.log(`📋 ${pattern.name} - Subject matches:`, matches);
        console.log(`📋 ${pattern.name} - Body matches:`, bodyMatches);
        
        // Deep analysis of each match
        if (bodyMatches.length > 0) {
          console.log(`🔬 Deep analysis of ${pattern.name} body matches:`);
          bodyMatches.forEach((match, index) => {
            console.log(`  Match ${index + 1}: "${match}"`);
            console.log(`  Position in body: ${selectedTemplate.body.indexOf(match)}`);
          });
        }
      });
      
      const uniquePlaceholders = new Set();

      console.log("🔍 STEP 3: Placeholder Extraction");
      if (isVisualTemplate) {
        console.log("📝 Extracting visual template placeholders...");
        // More specific regex that only matches {{word}} pattern
        // Excludes CSS properties by requiring word characters only - updated to include accented characters, spaces, and apostrophes
        const visualRegex = /\{\{([a-zA-ZÀ-ÿ][a-zA-Z0-9_À-ÿ\s']*)\}\}/g;

        const subjectMatches = [
          ...(selectedTemplate.subject.match(visualRegex) || []),
        ];
        const bodyMatches = [
          ...(selectedTemplate.body.match(visualRegex) || []),
        ];

        console.log("📋 Visual Subject matches:", subjectMatches);
        console.log("📋 Visual Body matches:", bodyMatches);

        subjectMatches.forEach((match, index) => {
          const placeholderName = match.slice(2, -2);
          console.log(`✅ Adding visual subject placeholder ${index + 1}: "${placeholderName}" from match "${match}"`);
          uniquePlaceholders.add(placeholderName);
        });

        bodyMatches.forEach((match, index) => {
          const placeholderName = match.slice(2, -2);
          console.log(`✅ Adding visual body placeholder ${index + 1}: "${placeholderName}" from match "${match}"`);
          uniquePlaceholders.add(placeholderName);
        });
      } else {
        console.log("📝 Extracting standard template placeholders...");
        // Standard regex for non-visual templates - updated to include accented characters, spaces, and apostrophes
        const standardRegex = /{([a-zA-ZÀ-ÿ][a-zA-Z0-9_À-ÿ\s']*)}/g;

        const subjectMatches = [
          ...(selectedTemplate.subject.match(standardRegex) || []),
        ];
        const bodyMatches = [
          ...(selectedTemplate.body.match(standardRegex) || []),
        ];

        console.log("📋 Standard Subject matches:", subjectMatches);
        console.log("📋 Standard Body matches:", bodyMatches);

        subjectMatches.forEach((match, index) => {
          const placeholderName = match.slice(1, -1);
          console.log(`✅ Adding standard subject placeholder ${index + 1}: "${placeholderName}" from match "${match}"`);
          uniquePlaceholders.add(placeholderName);
        });

        bodyMatches.forEach((match, index) => {
          const placeholderName = match.slice(1, -1);
          console.log(`✅ Adding standard body placeholder ${index + 1}: "${placeholderName}" from match "${match}"`);
          uniquePlaceholders.add(placeholderName);
        });
      }

      console.log("🔍 STEP 4: Final Placeholder List");
      console.log("📋 Unique placeholders found:", [...uniquePlaceholders]);
      console.log("📋 Total placeholders count:", uniquePlaceholders.size);
      setPlaceholders([...uniquePlaceholders]);

      // Initialize placeholder values
      console.log("🔍 STEP 5: Placeholder Value Initialization");
      const initialPlaceholderValues = {};
      
      uniquePlaceholders.forEach((placeholder, index) => {
        console.log(`🔧 Processing placeholder ${index + 1}/${uniquePlaceholders.size}: "${placeholder}"`);
        
        if (candidateInfo && sendMode === "single") {
          console.log("👤 Candidate info available:", candidateInfo);
          
          if (placeholder === "first_name" && (candidateInfo.firstName || candidateInfo.first_name)) {
            const value = candidateInfo.firstName || candidateInfo.first_name;
            console.log(`✅ Setting ${placeholder} to: "${value}"`);
            initialPlaceholderValues[placeholder] = value;
          } else if (placeholder === "last_name" && (candidateInfo.lastName || candidateInfo.last_name)) {
            const value = candidateInfo.lastName || candidateInfo.last_name;
            console.log(`✅ Setting ${placeholder} to: "${value}"`);
            initialPlaceholderValues[placeholder] = value;
          } else if (placeholder === "name" && (candidateInfo.firstName || candidateInfo.first_name) && (candidateInfo.lastName || candidateInfo.last_name)) {
            // Auto-fill full name if both first and last names are available
            const firstName = candidateInfo.firstName || candidateInfo.first_name || "";
            const lastName = candidateInfo.lastName || candidateInfo.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();
            console.log(`✅ Setting ${placeholder} to: "${fullName}"`);
            initialPlaceholderValues[placeholder] = fullName;
          }
          // Handle French placeholders
          else if ((placeholder === "prenom" || placeholder === "prénom") && (candidateInfo.firstName || candidateInfo.first_name)) {
            const value = candidateInfo.firstName || candidateInfo.first_name;
            console.log(`✅ Setting ${placeholder} to: "${value}"`);
            initialPlaceholderValues[placeholder] = value;
          } else if (placeholder === "nom_famille" && (candidateInfo.lastName || candidateInfo.last_name)) {
            const value = candidateInfo.lastName || candidateInfo.last_name;
            console.log(`✅ Setting ${placeholder} to: "${value}"`);
            initialPlaceholderValues[placeholder] = value;
          } else if (placeholder === "nom_complet" && (candidateInfo.firstName || candidateInfo.first_name) && (candidateInfo.lastName || candidateInfo.last_name)) {
            const firstName = candidateInfo.firstName || candidateInfo.first_name || "";
            const lastName = candidateInfo.lastName || candidateInfo.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();
            console.log(`✅ Setting ${placeholder} to: "${fullName}"`);
            initialPlaceholderValues[placeholder] = fullName;
          } else if (placeholder === "position" && candidateInfo.position) {
            console.log(`✅ Setting ${placeholder} to: "${candidateInfo.position}"`);
            initialPlaceholderValues[placeholder] = candidateInfo.position;
          } else if (placeholder === "company" && candidateInfo.company) {
            console.log(`✅ Setting ${placeholder} to: "${candidateInfo.company}"`);
            initialPlaceholderValues[placeholder] = candidateInfo.company;
          } else {
            console.log(`⚠️ Setting ${placeholder} to empty string (no match)`);
            initialPlaceholderValues[placeholder] = "";
          }
        } else {
          console.log(`⚠️ Setting ${placeholder} to empty string (no candidate info or multiple mode)`);
          initialPlaceholderValues[placeholder] = "";
        }
      });

      console.log("🔍 STEP 6: Final Placeholder Values");
      console.log("📋 Initial placeholder values:", initialPlaceholderValues);
      console.log("📋 Placeholder values count:", Object.keys(initialPlaceholderValues).length);
      
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          recipient_email:
            sendMode === "single" ? candidateInfo?.email || "" : "",
          placeholder_values: initialPlaceholderValues,
        };
        
        console.log("📋 Updated form data:", newFormData);
        console.log("📋 Form data placeholder_values:", newFormData.placeholder_values);
        return newFormData;
      });

      setRenderedEmail({
        subject: selectedTemplate.subject,
        body: selectedTemplate.body,
      });
      
      console.log("=== PLACEHOLDER EXTRACTION DEBUG END ===");
    }
  }, [selectedTemplate, candidateInfo, isVisualTemplate, sendMode]);

  // Update rendered email when form data changes
  useEffect(() => {
    if (selectedTemplate && formData.placeholder_values) {
      let renderedSubject = selectedTemplate.subject;
      let renderedBody = selectedTemplate.body;

      // Replace placeholders with values based on template type
      Object.entries(formData.placeholder_values).forEach(([key, value]) => {
        const placeholder = isVisualTemplate ? `{{${key}}}` : `{${key}}`;
        const escapedPlaceholder = placeholder.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        );

        console.log(`Frontend rendering: Replacing '${placeholder}' with '${value}'`);
        console.log(`Escaped placeholder: '${escapedPlaceholder}'`);

        renderedSubject = renderedSubject.replace(
          new RegExp(escapedPlaceholder, "g"),
          value || placeholder
        );

        renderedBody = renderedBody.replace(
          new RegExp(escapedPlaceholder, "g"),
          value || placeholder
        );
      });

      setRenderedEmail({
        subject: renderedSubject,
        body: renderedBody,
      });
    }
  }, [selectedTemplate, formData.placeholder_values, isVisualTemplate]);

  // Update iframe content when renderedEmail changes
  useEffect(() => {
    if (iframeRef.current && isVisualTemplate && renderedEmail.body) {
      // Get the iframe document
      const iframeDoc =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow.document;

      // Write the HTML content to the iframe
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              line-height: 1.5;
            }
            img { max-width: 100%; height: auto; }
            table { max-width: 100%; }
          </style>
        </head>
        <body>${renderedEmail.body}</body>
        </html>
      `);
      iframeDoc.close();
    }
  }, [renderedEmail.body, isVisualTemplate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePlaceholderChange = (placeholder, value) => {
    console.log(`Placeholder changed: ${placeholder} = "${value}"`);
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        placeholder_values: {
          ...prev.placeholder_values,
          [placeholder]: value,
        },
      };
      
      console.log("Updated placeholder values:", newFormData.placeholder_values);
      return newFormData;
    });

    // Clear validation error when field is edited
    if (errors[`placeholder_${placeholder}`]) {
      setErrors((prev) => ({ ...prev, [`placeholder_${placeholder}`]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate based on send mode
    if (sendMode === "single") {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.recipient_email.trim()) {
        newErrors.recipient_email = t("sendEmailModal.recipientEmailRequired");
      } else if (!emailRegex.test(formData.recipient_email)) {
        newErrors.recipient_email = t("sendEmailModal.validEmailRequired");
      }
    } else {
      // Validate candidate selection
      if (selectedCandidates.length === 0) {
        newErrors.candidates = t("sendEmailModal.selectAtLeastOneCandidate");
      }
    }

    // Validate if template is selected when in template selection mode
    if (showTemplateSelection && !selectedTemplate) {
      newErrors.template = t("sendEmailModal.pleaseSelectTemplate");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async () => {
    console.log("=== EMAIL SUBMIT DEBUG START ===");
    console.log("Send mode:", sendMode);
    console.log("Selected template:", selectedTemplate);
    console.log("Is visual template:", isVisualTemplate);
    console.log("Form data:", formData);
    console.log("Placeholders extracted:", placeholders);
    console.log("Placeholder values:", formData.placeholder_values);
    console.log("Selected candidates:", selectedCandidates);
    console.log("Candidate info:", candidateInfo);

    if (validate()) {
      setLoading(true);
      setStatus({ type: null, message: "" });

      try {
        // Make sure selectedTemplate and selectedTemplate.id are defined
        if (!selectedTemplate || !selectedTemplate.id) {
          throw new Error("No template selected");
        }

        if (sendMode === "single") {
          console.log("=== SINGLE EMAIL MODE ===");
          // Single email sending (existing logic)
          const payload = {
            template_id: selectedTemplate.id,
            recipient_email: formData.recipient_email,
            placeholder_values: formData.placeholder_values,
            is_visual_template: isVisualTemplate,
          };

          console.log("Single email payload:", JSON.stringify(payload, null, 2));

                     const response = await axios.post(
            `${process.env.REACT_APP_API_URL}recruiters/${recruiterId}/send-email/`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("Single email response:", response.data);

          setStatus({
            type: "success",
            message: t("sendEmailModal.emailSentSuccessfully"),
          });

          if (candidateInfo) {
            // Call handleCreateWaitList without awaiting to avoid blocking success flow
            handleCreateWaitList().catch(error => {
              console.error("Waitlist creation failed but email was sent successfully:", error);
            });
          }
                 } else {
          console.log("=== MULTIPLE EMAIL MODE ===");
          console.log("🔍 STEP 1: Multiple Email Processing Start");
          console.log("📋 Available placeholders:", placeholders);
          console.log("📋 Base placeholder values:", formData.placeholder_values);
          console.log("📋 Selected candidates:", selectedCandidates);
          console.log("📋 Total candidates available:", candidates.length);
          
          // Multiple email sending with candidate data auto-fill
          const selectedCandidateData = candidates
            .filter((candidate) => selectedCandidates.includes(candidate.id))
            .map((candidate, candidateIndex) => {
              console.log(`🔍 STEP 2: Processing candidate ${candidateIndex + 1}`);
              console.log("👤 Candidate data:", candidate);
              console.log("👤 Candidate ID:", candidate.id);
              console.log("👤 Candidate email:", candidate.email);
              console.log("👤 Candidate first_name:", candidate.first_name);
              console.log("👤 Candidate last_name:", candidate.last_name);
              
              // Create candidate-specific placeholder values
              const candidatePlaceholders = {};
              
              console.log("🔍 STEP 3: Mapping candidate data to placeholders");
              
              // Map candidate data to template placeholders
              if (placeholders.includes('first_name') || placeholders.includes('prenom') || placeholders.includes('prénom')) {
                candidatePlaceholders.first_name = candidate.first_name || "";
                candidatePlaceholders.prenom = candidate.first_name || "";
                candidatePlaceholders.prénom = candidate.first_name || "";
                console.log(`✅ Added first_name/prenom/prénom: "${candidate.first_name}"`);
              } else {
                console.log("⚠️ No first_name/prenom/prénom placeholders found in template");
              }
              
              if (placeholders.includes('last_name') || placeholders.includes('nom_famille')) {
                candidatePlaceholders.last_name = candidate.last_name || "";
                candidatePlaceholders.nom_famille = candidate.last_name || "";
                console.log(`✅ Added last_name/nom_famille: "${candidate.last_name}"`);
              } else {
                console.log("⚠️ No last_name/nom_famille placeholders found in template");
              }
              
              if (placeholders.includes('name') || placeholders.includes('nom_complet')) {
                const fullName = `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim();
                candidatePlaceholders.name = fullName;
                candidatePlaceholders.nom_complet = fullName;
                console.log(`✅ Added name/nom_complet: "${fullName}"`);
              } else {
                console.log("⚠️ No name/nom_complet placeholders found in template");
              }
              
              // Add support for email
              if (placeholders.includes('email')) {
                candidatePlaceholders.email = candidate.email || "";
                console.log(`✅ Added email: "${candidate.email}"`);
              }
              
              console.log("🔍 STEP 4: Placeholder Merging");
              // Merge with base placeholders (user-defined values)
              const finalPlaceholders = { ...formData.placeholder_values, ...candidatePlaceholders };
              
              console.log("📋 Base placeholders:", formData.placeholder_values);
              console.log("📋 Candidate placeholders:", candidatePlaceholders);
              console.log("📋 Final merged placeholders:", finalPlaceholders);
              console.log("📋 Final placeholders count:", Object.keys(finalPlaceholders).length);
              
              // CRITICAL DEBUG: Check if prénom is in final placeholders
              if ('prénom' in finalPlaceholders) {
                console.log(`✅ prénom found in final placeholders: "${finalPlaceholders['prénom']}"`);
              } else {
                console.log(`❌ prénom NOT found in final placeholders!`);
                console.log("Available keys:", Object.keys(finalPlaceholders));
              }
              
              const candidateData = {
                email: candidate.email,
                placeholder_values: finalPlaceholders,
                candidate_data: {
                  first_name: candidate.first_name || "",
                  last_name: candidate.last_name || "",
                  email: candidate.email || "",
                  phone: candidate.phone || "",
                  company: candidate.company || "",
                  skills: candidate.skills || [],
                },
              };
              
              console.log("📋 Final candidate data for backend:", candidateData);
              return candidateData;
            });

          const payload = {
            template_id: selectedTemplate.id,
            recipient_emails: selectedCandidateData,
            is_visual_template: isVisualTemplate,
          };

          console.log("Multiple email payload:", JSON.stringify(payload, null, 2));
          console.log("Number of recipients:", selectedCandidateData.length);

          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}recruiters/${recruiterId}/send-email/`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("Multiple email response:", response.data);

          // Handle different response statuses
          if (response.status === 200) {
            setStatus({
              type: "success",
              message:
                response.data.message ||
                t("sendEmailModal.emailsSentSuccessfully", {
                  count: selectedCandidates.length,
                }),
            });
          } else if (response.status === 207) {
            // Partial success
            setStatus({
              type: "warning",
              message:
                response.data.message || t("sendEmailModal.someEmailsFailed"),
            });
          }
        }

        // Close the modal after a delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        console.error("=== EMAIL SUBMIT ERROR ===");
        console.error("Error object:", error);
        console.error("Error response:", error.response?.data);

        let errorMessage = t("sendEmailModal.failedToSendEmail");

        if (
          error.response?.data?.errors &&
          Array.isArray(error.response.data.errors)
        ) {
          // Show first few errors if multiple
          errorMessage = error.response.data.errors.slice(0, 3).join("; ");
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }

        setStatus({
          type: "error",
          message: errorMessage,
        });
      } finally {
        console.log("=== EMAIL SUBMIT DEBUG END ===");
        setLoading(false);
      }
    } else {
      console.log("Validation failed, errors:", errors);
    }
  };

  const renderEmailPreview = () => {
    // ALWAYS render HTML here
    return (
      <Box
        sx={{ width: "100%", height: "auto", backgroundColor: "#fff", p: 2 }}
      >
        {(() => {
          const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(renderedEmail.body || "");
          const htmlContent = hasHtmlTags
            ? renderedEmail.body
            : (renderedEmail.body || "").replace(/\n/g, "<br/>");
          return (
            <div
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{ fontFamily: "Arial, sans-serif", lineHeight: 1.5, whiteSpace: 'pre-wrap' }}
            />
          );
        })()}
      </Box>
    );
  };

  const { t } = useTranslation();



  // Effect to ensure placeholders are initialized when both template and candidate info are available
  useEffect(() => {
    if (selectedTemplate && candidateInfo && sendMode === "single") {
      // If we have placeholders but no values, re-initialize them
      if (placeholders.length > 0 && Object.keys(formData.placeholder_values).length === 0) {
        const initialPlaceholderValues = {};
        placeholders.forEach((placeholder) => {
          if (placeholder === "first_name" && (candidateInfo.firstName || candidateInfo.first_name)) {
            initialPlaceholderValues[placeholder] = candidateInfo.firstName || candidateInfo.first_name;
          } else if (placeholder === "last_name" && (candidateInfo.lastName || candidateInfo.last_name)) {
            initialPlaceholderValues[placeholder] = candidateInfo.lastName || candidateInfo.last_name;
          } else if (placeholder === "name" && (candidateInfo.firstName || candidateInfo.first_name) && (candidateInfo.lastName || candidateInfo.last_name)) {
            const firstName = candidateInfo.firstName || candidateInfo.first_name || "";
            const lastName = candidateInfo.lastName || candidateInfo.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();
            initialPlaceholderValues[placeholder] = fullName;
          }
          // Handle French placeholders
          else if (placeholder === "prenom" && (candidateInfo.firstName || candidateInfo.first_name)) {
            initialPlaceholderValues[placeholder] = candidateInfo.firstName || candidateInfo.first_name;
          } else if (placeholder === "nom_famille" && (candidateInfo.lastName || candidateInfo.last_name)) {
            initialPlaceholderValues[placeholder] = candidateInfo.lastName || candidateInfo.last_name;
          } else if (placeholder === "nom_complet" && (candidateInfo.firstName || candidateInfo.first_name) && (candidateInfo.lastName || candidateInfo.last_name)) {
            const firstName = candidateInfo.firstName || candidateInfo.first_name || "";
            const lastName = candidateInfo.lastName || candidateInfo.last_name || "";
            const fullName = `${firstName} ${lastName}`.trim();
            initialPlaceholderValues[placeholder] = fullName;
          } else if (placeholder === "position" && candidateInfo.position) {
            initialPlaceholderValues[placeholder] = candidateInfo.position;
          } else if (placeholder === "company" && candidateInfo.company) {
            initialPlaceholderValues[placeholder] = candidateInfo.company;
          } else {
            initialPlaceholderValues[placeholder] = "";
          }
        });
        
        setFormData((prev) => ({
          ...prev,
          placeholder_values: initialPlaceholderValues,
        }));
      }
    }
  }, [selectedTemplate, candidateInfo, sendMode, placeholders, formData.placeholder_values]);

  // Infinite scroll effect for candidate list
  useEffect(() => {
    if (!candidateContainerRef) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = candidateContainerRef;
      // Trigger loading when user is 50px from the bottom for better UX
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (
        nearBottom &&
        candidateHasMore &&
        !loadingCandidates &&
        !loadingMoreCandidates
      ) {
        loadMoreCandidates();
      }
    };

    candidateContainerRef.addEventListener("scroll", handleScroll);
    return () =>
      candidateContainerRef.removeEventListener("scroll", handleScroll);
  }, [
    candidateContainerRef,
    candidateHasMore,
    loadingCandidates,
    loadingMoreCandidates,
  ]);

  // Function to load more candidates
  const loadMoreCandidates = async () => {
    try {
      setLoadingMoreCandidates(true);
      const nextPage = candidatePage + 1;
      await fetchCandidates(candidateSearch, nextPage);
    } catch (error) {
      console.error("Error loading more candidates:", error);
    } finally {
      setLoadingMoreCandidates(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={!loading ? onClose : undefined}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 32px rgba(0, 23, 231, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SendIcon sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {showTemplateSelection
                ? t("sendEmailModal.sendEmailUsingTemplate")
                : t("sendEmailModal.sendEmailUsing", {
                    name: selectedTemplate?.name || t("sendEmailModal.template"),
                  })}
            </Typography>
          </Box>
          {!loading && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label={t("sendEmailModal.close")}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {status.type && (
            <Alert
              severity={status.type}
              sx={{ mb: 2 }}
              onClose={() => setStatus({ type: null, message: "" })}
            >
              {status.message}
            </Alert>
          )}

          <Box sx={{ p: 1 }}>
            {/* NEW: Send Mode Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">
                {t("sendEmailModal.sendMode")}
              </FormLabel>
              <RadioGroup value={sendMode} onChange={handleSendModeChange} row>
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label={t("sendEmailModal.singleRecipient")}
                />
                <FormControlLabel
                  value="multiple"
                  control={<Radio />}
                  label={t("sendEmailModal.multipleRecipients")}
                />
              </RadioGroup>
            </FormControl>

            {/* Template Selection if needed */}
            {showTemplateSelection && (
              <FormControl
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.template}
              >
                <InputLabel id="template-select-label">
                  {t("sendEmailModal.selectTemplate")}
                </InputLabel>
                <Select
                  labelId="template-select-label"
                  id="template-select"
                  value={selectedTemplateId}
                  onChange={handleTemplateChange}
                  label={t("sendEmailModal.selectTemplate")}
                  disabled={loading || loadingTemplates}
                  ref={selectRef}
                                     MenuProps={{
                     PaperProps: {
                       onScroll: handleMenuScroll,
                       style: { maxHeight: 300 },
                     },
                   }}
                >
                  <MenuItem value="">
                    <em>{t("sendEmailModal.selectATemplate")}</em>
                  </MenuItem>
                  {availableTemplates.length > 0 ? (
                    availableTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}{" "}
                        {template.design ? t("sendEmailModal.visual") : ""}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>{t("sendEmailModal.noTemplatesAvailable")}</em>
                    </MenuItem>
                  )}
                  {isLoadingMore && (
                    <MenuItem disabled style={{ justifyContent: "center" }}>
                      <CircularProgress size={20} />
                    </MenuItem>
                  )}
                  {!isLoadingMore && hasMore && (
                    <MenuItem
                                             onClick={(e) => {
                         e.stopPropagation();
                         const nextPage = page + 1;
                         setPage(nextPage);
                         fetchTemplates(nextPage, true);
                       }}
                      style={{ justifyContent: "center" }}
                    >
                      <Box display="flex" alignItems="center">
                        <MoreHorizIcon sx={{ mr: 1 }} />
                        {t("sendEmailModal.loadMoreTemplates")}
                      </Box>
                    </MenuItem>
                  )}
                </Select>
                {errors.template && (
                  <Typography color="error" variant="caption">
                    {errors.template}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Only show the rest if we have a selected template or in non-selection mode */}
            {(selectedTemplate || !showTemplateSelection) && (
              <>
                {/* NEW: Recipients Section */}
                {sendMode === "single" ? (
                  <TextField
                    label={t("sendEmailModal.recipientEmail")}
                    name="recipient_email"
                    value={formData.recipient_email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={!!errors.recipient_email}
                    helperText={errors.recipient_email}
                    disabled={loading}
                    autoFocus={!showTemplateSelection}
                  />
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      {t("sendEmailModal.selectCandidates")}
                    </Typography>
                    {/* Step 3: Search input field */}
                    <TextField
                      label={t("sendEmailModal.searchCandidates")}
                      variant="outlined"
                      fullWidth
                      value={candidateSearch}
                      onChange={(e) => {
                        setCandidateSearch(e.target.value);
                        fetchCandidates(e.target.value, 1); // Reset to page 1
                      }}
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: candidateSearch && (
                          <IconButton
                            onClick={() => {
                              setCandidateSearch("");
                              fetchCandidates("", 1);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAllCandidates}
                        disabled={loadingCandidates}
                        sx={{
                          borderRadius: 2,
                          borderColor: "#0017E7",
                          color: "#0017E7",
                          fontWeight: 500,
                          "&:hover": {
                            borderColor: "#0010a3",
                            backgroundColor: "rgba(0, 23, 231, 0.04)",
                          },
                        }}
                      >
                        {loadingCandidates
                          ? t("sendEmailModal.loading")
                          : selectedCandidates.length > 0
                          ? t("sendEmailModal.deselectAll")
                          : `${t("sendEmailModal.selectAll")} ${
                              totalCandidateCount > 0
                                ? `(${totalCandidateCount})`
                                : ""
                            }`}
                      </Button>
                      {selectedCandidates.length > 0 && (
                        <Chip
                          label={`${selectedCandidates.length} ${
                            t("sendEmailModal.selected")
                          }`}
                          color="primary"
                          size="small"
                          sx={{
                            background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                            color: "white",
                            fontWeight: 500,
                          }}
                        />
                      )}
                    </Box>
                    {/* Step 2: Candidate list with infinite scroll and loading indicator */}
                    <Card
                      variant="outlined"
                      sx={{ 
                        maxHeight: 200, 
                        overflow: "auto",
                        borderRadius: 2,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                      }}
                      ref={(ref) => setCandidateContainerRef(ref)}
                    >
                      <List dense>
                        {candidates.map((candidate) => (
                          <ListItem
                            key={`${candidate.candidate_type}-${candidate.source_id}`}
                            button
                            onClick={() =>
                              handleCandidateSelection(candidate.id)
                            }
                          >
                            <ListItemIcon>
                              <Checkbox
                                checked={selectedCandidates.includes(
                                  candidate.id
                                )}
                                onChange={() =>
                                  handleCandidateSelection(candidate.id)
                                }
                              />
                            </ListItemIcon>
                            <ListItemIcon>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {candidate.image ? (
                                  <img
                                    src={candidate.image}
                                    alt={`${candidate.first_name} ${candidate.last_name}`}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <PersonIcon />
                                )}
                              </Avatar>
                            </ListItemIcon>
                                                         <ListItemText
                               primary={
                                 candidate.first_name || candidate.last_name
                                   ? `${candidate.first_name || ""} ${
                                       candidate.last_name || ""
                                     }`.trim()
                                   : candidate.email.split("@")[0] // Fallback to email prefix
                               }
                               secondary={
                                 <>
                                   <Typography
                                     variant="body2"
                                     color="textSecondary"
                                     component="span"
                                   >
                                     {candidate.email}
                                   </Typography>
                                   {candidate.company && (
                                     <Typography
                                       variant="caption"
                                       color="textSecondary"
                                       component="span"
                                       sx={{ display: "block" }}
                                     >
                                       {candidate.company}
                                     </Typography>
                                   )}
                                 </>
                               }
                             />
                          </ListItem>
                        ))}
                        {/* Loading indicator at the bottom */}
                        {(loadingCandidates || loadingMoreCandidates) && (
                          <ListItem>
                            <Box
                              display="flex"
                              justifyContent="center"
                              width="100%"
                            >
                              <CircularProgress size={24} />
                            </Box>
                          </ListItem>
                        )}
                        {/* End of list indicator */}
                        {!candidateHasMore && candidates.length > 0 && (
                          <ListItem>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              textAlign="center"
                              width="100%"
                            >
                              {t("sendEmailModal.endOfCandidateList")}
                            </Typography>
                          </ListItem>
                        )}
                      </List>
                    </Card>
                    {/* Infinite scroll loading indicator */}
                    {loadingMoreCandidates && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {t("sendEmailModal.loadingMoreCandidates")}
                        </Typography>
                      </Box>
                    )}

                    {errors.candidates && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors.candidates}
                      </Typography>
                    )}
                  </Box>
                )}

                {placeholders.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.primary.main,
                      }}
                    >
                      {t("sendEmailModal.customizeYourEmail")}
                    </Typography>
                                         <Typography variant="body2" color="textSecondary" paragraph>
                       {sendMode === "multiple" 
                         ? "Personalized placeholders will be auto-filled for each candidate. You can add custom values that will apply to all emails."
                         : t("sendEmailModal.fillPlaceholders")
                       }
                     </Typography>
                    
                    

                                         {placeholders.map((placeholder) => {
                       // Define auto-filled placeholders for multiple mode
                       const autoFilledPlaceholders = [
                         "first_name", "last_name", "name",
                         "prenom", "nom_famille", "nom_complet"
                       ];
                       
                       if (sendMode === "multiple" && autoFilledPlaceholders.includes(placeholder)) {
                         // Show auto-filled placeholders as read-only with info
                         return (
                           <TextField
                             key={placeholder}
                             label={
                               placeholder.charAt(0).toUpperCase() +
                               placeholder.slice(1).replace("_", " ")
                             }
                             value={t("sendEmailModal.autoFilledPerCandidate")}
                             fullWidth
                             margin="normal"
                             variant="outlined"
                             disabled={true}
                             helperText={t("sendEmailModal.autoFilledTooltip")}
                             InputProps={{
                               readOnly: true,
                               sx: { backgroundColor: 'grey.100' }
                             }}
                           />
                         );
                       }
                       
                       return (
                         <TextField
                           key={placeholder}
                           label={
                             placeholder.charAt(0).toUpperCase() +
                             placeholder.slice(1).replace("_", " ")
                           }
                           value={formData.placeholder_values[placeholder] || ""}
                           onChange={(e) =>
                             handlePlaceholderChange(placeholder, e.target.value)
                           }
                           fullWidth
                           margin="normal"
                           variant="outlined"
                           error={!!errors[`placeholder_${placeholder}`]}
                           helperText={errors[`placeholder_${placeholder}`]}
                           disabled={loading}
                         />
                       );
                     })}
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
                    {t("sendEmailModal.emailPreview")}
                  </Typography>
                </Box>

                <Card variant="outlined" sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                      {t("sendEmailModal.subject")}: {renderedEmail.subject}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    {renderEmailPreview()}
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: "space-between", gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              borderColor: "#0017E7",
              color: "#0017E7",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#0010a3",
                backgroundColor: "rgba(0, 23, 231, 0.04)",
              },
            }}
          >
            {t("sendEmailModal.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (!selectedTemplate && showTemplateSelection)}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : <SendIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.2,
              fontWeight: 600,
              background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
              boxShadow: "0px 4px 12px rgba(0, 23, 231, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                boxShadow: "0px 6px 16px rgba(0, 23, 231, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#ccc",
                color: "#666",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {loading
              ? t("sendEmailModal.sending")
              : t("sendEmailModal.sendEmail")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default SendEmailModal;
